import moment from 'moment-timezone';
import MomentService from 'cio/services/moment';
import { IN_APP_EXPIRATION_THRESHOLD_DAYS } from './in-app/message-config';

export type TimeRange = {
  start?: moment.Moment;
  end?: moment.Moment;
};

export function unixNowSeconds() {
  return Math.floor(Date.now() / 1000);
}

export function unixNowMilliseconds() {
  return Date.now();
}

export function logTimeElapsed<T>(name: string, callback: (...args: any[]) => T, log = false): T {
  let startTime = performance.now();

  try {
    return callback();
  } finally {
    let endTime = performance.now();
    if (log) {
      // eslint-disable-next-line no-console
      console.log(`${name}: ${(endTime - startTime) / 1000}s`);
    }
  }
}

export function getMomentUtc(date: moment.Moment) {
  return moment.tz(`${date.format('YYYY-MM-DD')} 00:00:00`, 'Etc/UTC');
}

export function getTimeRangeUtc(timeRange: TimeRange): TimeRange | undefined {
  let { start, end } = timeRange;

  if (start === undefined || end === undefined) {
    return;
  }

  return {
    start: getMomentUtc(start),
    end: getMomentUtc(end),
  };
}

// Will return a date string formatted in the one of the following ways:
//   - "today at 1:23:45 pm (EDT)" if the date is yesterday, today, or tomorrow
//   - "Aug 12, 2022 at 1:23:45 pm (EDT)" in all other cases
export function timestampToInlineLongDate(timestamp: number | null | undefined, momentService: MomentService): string {
  if (timestamp == null) {
    return '';
  }

  let isWithinOneDayFromNow = dateIsWithinOneDayFromNow(timestamp);
  let finalFormatter = (m: moment.Moment) => m.calendar();

  return momentService.formatTimestamp([timestamp], {
    capitalize: !isWithinOneDayFromNow,
    finalFormatter,
  });
}

// Say if a timestamp is yesterday, today, or tomorrow from now.
export function dateIsWithinOneDayFromNow(timestamp: number): boolean {
  let from = moment().subtract(1, 'days').startOf('day').unix();
  let to = moment().add(1, 'days').endOf('day').unix();

  return timestamp >= from && timestamp <= to;
}

export function dateIsWithinThreshold(timestamp?: number, thresholdInDays = IN_APP_EXPIRATION_THRESHOLD_DAYS): boolean {
  if (isNaN(Number(timestamp))) {
    return false;
  }
  let now = moment().unix();
  let thresholdDaysFromNow = moment().add(thresholdInDays, 'days').unix();
  return timestamp != null && timestamp >= now && timestamp <= thresholdDaysFromNow;
}
